import { buildChildrenByParent } from "./chatbotTreeModel";
import { buildSlotPath, slotStepName } from "../../utils/chatbotNavigationActions";

export function collectDescendantIds(slotId, slots = []) {
  const childrenByParent = buildChildrenByParent(slots);
  const out = new Set();
  const walk = (id) => {
    const children = childrenByParent.get(String(id)) || [];
    for (const child of children) {
      const cid = Number(child.id);
      if (out.has(cid)) continue;
      out.add(cid);
      walk(cid);
    }
  };
  walk(Number(slotId));
  return out;
}

/**
 * Opções válidas de pai para reparent de um slot (espelha regras do backend).
 */
export function buildValidParentOptions(currentSlot, allSlots = []) {
  if (!currentSlot) return [];

  const currentId = Number(currentSlot.id);
  const descendants = collectDescendantIds(currentId, allSlots);
  const slotById = new Map((allSlots || []).map((s) => [Number(s.id), s]));
  const isStart = isFlowStartSlot(currentSlot, allSlots);

  const options = [];

  if (!isStart) {
    options.push({
      value: 0,
      label: "Raiz do fluxo (sem pai)",
      path: "",
      searchText: "raiz fluxo sem pai nível superior",
    });
  }

  for (const s of allSlots || []) {
    const id = Number(s.id);
    if (id === currentId) continue;
    if (descendants.has(id)) continue;
    if (String(s.identificador || "").trim() === "Exceção") continue;

    const path = buildSlotPath(s.id, slotById);
    const label = slotStepName(s);
    options.push({
      value: id,
      label,
      path,
      searchText: `${label} ${path} ${s.identificador || ""} ${s.nome || ""} ${id}`.toLowerCase(),
    });
  }

  options.sort((a, b) => {
    if (a.value === 0) return -1;
    if (b.value === 0) return 1;
    return String(a.path || a.label).localeCompare(String(b.path || b.label), "pt-BR");
  });

  return options;
}

export function getParentOptionLabel(option) {
  if (!option) return "";
  if (option.value === 0) return option.label;
  return option.path ? `${option.label} — ${option.path}` : option.label;
}

export function findParentOption(options, idPai) {
  const pid = Number(idPai) || 0;
  return (options || []).find((o) => Number(o.value) === pid) || null;
}

function compareSlotOrder(a, b) {
  return Number(a?.ordem || 0) - Number(b?.ordem || 0) || Number(a?.id || 0) - Number(b?.id || 0);
}

/**
 * Primeiro passo raiz não-global (o motor entra aqui na 1ª mensagem, sem olhar identificador).
 */
export function findInicioSlot(slots = []) {
  const named = (slots || []).find(
    (s) => Number(s.id_pai) === 0 && String(s.identificador || "").trim() === "Início"
  );
  if (named) return named;
  const candidates = (slots || []).filter((s) => {
    if (Number(s.id_pai) !== 0) return false;
    if (String(s.identificador || "").trim() === "Exceção") return false;
    if (Number(s.global) === 1) return false;
    return true;
  });
  candidates.sort(compareSlotOrder);
  return candidates[0] || null;
}

/** Passo em que a conversa começa: identificador “Início” ou primeiro raiz não-global. */
export function isFlowStartSlot(slot, slots = []) {
  if (!slot) return false;
  if (String(slot.identificador || "").trim() === "Início") return true;
  const inicio = findInicioSlot(slots);
  return !!inicio && Number(inicio.id) === Number(slot.id);
}

export function hasLocalExceptionForParent(slots = [], parentId) {
  const pid = Number(parentId);
  return (slots || []).some(
    (s) => Number(s.id_pai) === pid && String(s.identificador || "").trim() === "Exceção"
  );
}

export function isGlobalExceptionSlot(slot, slots = []) {
  if (!slot || String(slot.identificador || "").trim() !== "Exceção") return false;
  const inicio = findInicioSlot(slots);
  return !!inicio && Number(slot.id_pai) === Number(inicio.id);
}

export function findGlobalExceptionSlot(slots = []) {
  const inicio = findInicioSlot(slots);
  if (!inicio) return null;
  return (
    (slots || []).find(
      (s) =>
        Number(s.id_pai) === Number(inicio.id) &&
        String(s.identificador || "").trim() === "Exceção"
    ) || null
  );
}

export function isConfiguredExceptionSlot(slot) {
  if (!slot) return false;
  if (String(slot.resposta || "").trim()) return true;
  const botoes = slot.botoes;
  if (Array.isArray(botoes) && botoes.some((b) => String(b || "").trim())) return true;
  if (typeof botoes === "string" && botoes.trim()) return true;
  return false;
}

export function hasConfiguredGlobalException(slots = []) {
  return isConfiguredExceptionSlot(findGlobalExceptionSlot(slots));
}

export function isLocalExceptionSlot(slot, slots = []) {
  return !!slot && String(slot.identificador || "").trim() === "Exceção" && !isGlobalExceptionSlot(slot, slots);
}

export function canDeleteSlot(slot, slots = []) {
  if (!slot) return false;
  if (isFlowStartSlot(slot, slots)) return false;
  if (isGlobalExceptionSlot(slot, slots)) return false;
  return true;
}
